/**
 * Save helpers for the Vectorize tool. Two destinations, picked by
 * the in-editor `VectorizeSaveDialog`:
 *
 *   • Local download — anchor-element download. Browser drops the
 *     file in the user's default download folder. No OS-native save
 *     dialog: we deliberately don't use `showSaveFilePicker` here
 *     because it requires a user gesture that has already been
 *     consumed by the API call earlier in the flow, and matching the
 *     ExportDialog's local-download behavior keeps the editor's
 *     two save dialogs visually + functionally consistent. iOS Safari
 *     gets the share-sheet workaround the existing useImageExport
 *     hook also uses — it ignores `download` on anchors otherwise.
 *
 *   • Fias — the host Save dialog: the user picks a folder of their
 *     Documents and what to do about a name clash, and the bytes go up on
 *     the editor's `/save-structured` route with the tab's source, so the
 *     SVG keeps at least the tier of the file it was traced from
 *     (ADR-039 amendment 2).
 */

import api, { type VaultSaveSourceDto } from '@/lib/api';
import { arrayBufferToBase64 } from './bytes-base64';
import type { PromptSave } from '../hooks/useImageExport';

export type SaveSvgOutcome = 'saved' | 'failed';

/**
 * Download the SVG locally via an anchor element. Always reports
 * `'saved'` on success — the browser handles the rest of the flow
 * without exposing whether the user cancelled, so we have no way to
 * distinguish saved-vs-cancelled from JS. iOS Safari is special-cased
 * to use the share sheet because it ignores `download=` on anchors.
 */
export async function downloadSvgLocal(svg: string, fileName: string): Promise<SaveSvgOutcome> {
  const fullName = ensureSvgExtension(fileName);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  try {
    const isIOS =
      typeof navigator !== 'undefined' &&
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !('MSStream' in window);
    if (isIOS && typeof navigator !== 'undefined' && 'share' in navigator) {
      const file = new File([blob], fullName, { type: blob.type });
      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      if (!nav.canShare || nav.canShare({ files: [file] })) {
        try {
          await nav.share({ files: [file], title: fullName });
          return 'saved';
        } catch {
          // Share failed (cancel or otherwise) — fall through to
          // anchor download. Cancel on iOS share is indistinguishable
          // from genuine errors, so we just retry the anchor path.
        }
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fullName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return 'saved';
  } catch {
    return 'failed';
  }
}

/**
 * Save the SVG into the user's Documents, where they choose in the Save
 * dialog. Resolves the saved name, or `'cancelled'`. Save errors show in the
 * dialog, which stays open for another try. `derivedFrom` is the tab's
 * source: the trace is made from it, so it keeps at least its tier. The SVG
 * is a separate file, so it never becomes the tab's source.
 */
export async function saveSvgToFias(
  svg: string,
  fileName: string,
  promptSave: PromptSave,
  derivedFrom: VaultSaveSourceDto | null = null,
): Promise<{ name: string } | 'cancelled'> {
  const bytesBase64 = arrayBufferToBase64(new TextEncoder().encode(svg).buffer as ArrayBuffer);
  const saved = await promptSave({
    suggestedName: ensureSvgExtension(fileName),
    mimeType: 'image/svg+xml',
    saver: { kind: 'user', label: 'Image Editor', memoryKey: 'arche_image_editor' },
    writer: async (target) => {
      const result = await api.saveImageEditorStructured({
        format: 'svg',
        bytesBase64,
        fileName: target.name,
        target,
        ...(derivedFrom ? { derivedFrom } : {}),
      });
      return { documentId: result.fileId, name: result.fileName };
    },
  });
  return saved ? { name: saved.name } : 'cancelled';
}

function ensureSvgExtension(name: string): string {
  return /\.svg$/i.test(name) ? name : `${name}.svg`;
}
